import React from 'react';
import prettyBytes from 'pretty-bytes';
import { Box, styled, useTheme } from '@mui/material';
import { IconPaperclip, IconTrash } from '@tabler/icons-react';

import IconButton from '../../../IconButton';
import Typography from '../../../Typography';
import { FileAttachment } from '../../types';

interface AttachmentProps {
  attachment: FileAttachment;
  hasContainer?: boolean;
  onTrashIconClicked?: () => void;
  showTrashIcon?: boolean;
}

const Attachment: React.FC<AttachmentProps> = ({
  attachment,
  hasContainer = false,
  onTrashIconClicked,
  showTrashIcon = false,
}) => {
  const { palette } = useTheme();

  const attachmentComponent = (
    <Box alignItems='center' display='flex' gap='10px'>
      {attachment.content_type?.includes('image') ? (
        <ImageHolder url={attachment.url} />
      ) : (
        <Box
          sx={{
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'flex',
            height: '48px',
            justifyContent: 'center',
            width: '48px',
          }}
        >
          <IconPaperclip color={palette.colors.purple[200]} size={40} />
        </Box>
      )}

      <Box
        display='flex'
        flexDirection='column'
        gap={1}
        whiteSpace='break-spaces'
      >
        <Typography color={palette.colors.black} variant='font14'>
          {attachment.name}
        </Typography>
        {attachment.filesize && (
          <Typography color={palette.colors.grey[600]} variant='font12'>
            {prettyBytes(attachment.filesize)}
          </Typography>
        )}
      </Box>
      {showTrashIcon && (
        <Box alignItems='center' display='flex' ml='auto'>
          <IconButton
            aria-label={`Remove file ${attachment.name}`}
            onClick={() => {
              if (onTrashIconClicked) {
                onTrashIconClicked();
              }
            }}
            variant='ghost'
          >
            <IconTrash color={palette.colors.purple[500]} size={20} />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  if (hasContainer) {
    return (
      <AttachmentContainer href={attachment.url} rel='noopener' target='_blank'>
        {attachmentComponent}
      </AttachmentContainer>
    );
  }

  return attachmentComponent;
};

const AttachmentContainer = styled('a')`
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  cursor: pointer;
  padding: 6px 10px;
  text-decoration: none;

  &:hover {
    box-shadow:
      0px 2px 6px 0px rgba(3, 17, 38, 0.11),
      0px 0px 1px 0px rgba(0, 12, 32, 0.04);
  }
`;

const ImageHolder = styled('div')<{ url: string }>`
  background-image: url(${props => props.url});
  height: 48px;
  width: 48px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
`;

export default Attachment;
